import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, of, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { buildFinancialApiUrl } from '../utils/financial-api-url.util';
import { ErrorService } from '../shared/services/error.service';

export interface TrafficFineDriverIndication {
  id?: number;
  driverName: string;
  documentNumberCPF?: string;
  documentNumberRG?: string;
  address?: string;
  cep?: string;
  city?: string;
  state?: string;
  /** Vincula uma multa (POST exige ao menos trafficFineId ou trafficFineIds). */
  trafficFineId?: number;
  /** Vincula várias multas de uma vez. */
  trafficFineIds?: number[];
  userName?: string;
  isDeleted?: boolean;
  [key: string]: any;
}

export interface TrafficFineDriverIndicationPagedResult {
  items: any[];
  page: number;
  pageSize: number;
  totalItems: number;
  totalPages: number;
}

export interface PaginationOptions {
  page?: number;
  pageSize?: number;
  trafficFineId?: number;
}

@Injectable({ providedIn: 'root' })
export class TrafficFineDriverIndicationService {
  private financialUrl(path = ''): string {
    return buildFinancialApiUrl('trafficfine-driver-indications', path);
  }

  constructor(private http: HttpClient, private errorService: ErrorService) {}

  getAll(options?: PaginationOptions): Observable<TrafficFineDriverIndicationPagedResult> {
    let params = new HttpParams();
    if (options?.page) params = params.set('page', options.page.toString());
    if (options?.pageSize) params = params.set('pageSize', options.pageSize.toString());
    if (options?.trafficFineId != null) {
      params = params.set('trafficFineId', options.trafficFineId.toString());
    }

    return this.http.get<any>(this.financialUrl(), { params }).pipe(
      map(resp => this.mapPagedResponse(resp)),
      catchError(err => {
        this.errorService.showErrorMessage('Erro ao buscar indicações de condutor');
        return throwError(() => err);
      })
    );
  }

  getById(id: number): Observable<TrafficFineDriverIndication> {
    return this.http.get<any>(this.financialUrl(`/${id}`)).pipe(
      map(resp => this.normalizeIndication(this.unwrapSingleResponse(resp))),
      catchError(err => {
        this.errorService.showErrorMessage('Erro ao buscar indicação');
        return throwError(() => err);
      })
    );
  }

  /** Retorna null em 404/erro — útil quando a FK da multa aponta para registro inexistente. */
  tryGetById(id: number): Observable<TrafficFineDriverIndication | null> {
    return this.http.get<any>(this.financialUrl(`/${id}`)).pipe(
      map(resp => this.normalizeIndication(this.unwrapSingleResponse(resp))),
      catchError(() => of(null))
    );
  }

  /** Busca a indicação vinculada à multa via GET ?trafficFineId={id}. */
  getByTrafficFineId(trafficFineId: number): Observable<TrafficFineDriverIndication | null> {
    return this.getAll({ trafficFineId, page: 1, pageSize: 1 }).pipe(
      map(result => result.items[0] ?? null),
      catchError(err => {
        this.errorService.showErrorMessage('Erro ao buscar indicação da multa');
        return throwError(() => err);
      })
    );
  }

  /** Retorna null em 404/erro — fallback quando a multa não traz driverIndicationId. */
  tryGetByTrafficFineId(trafficFineId: number): Observable<TrafficFineDriverIndication | null> {
    return this.getAll({ trafficFineId, page: 1, pageSize: 1 }).pipe(
      map(result => result.items[0] ?? null),
      catchError(() => of(null))
    );
  }

  create(payload: TrafficFineDriverIndication): Observable<TrafficFineDriverIndication> {
    return this.http.post<any>(this.financialUrl(), payload).pipe(
      map(resp => this.normalizeIndication(this.unwrapSingleResponse(resp))),
      catchError(err => {
        this.errorService.showErrorMessage('Erro ao criar indicação');
        return throwError(() => err);
      })
    );
  }

  update(id: number, payload: Partial<TrafficFineDriverIndication>): Observable<TrafficFineDriverIndication> {
    return this.http.put<any>(this.financialUrl(`/${id}`), payload).pipe(
      map(resp => this.normalizeIndication(this.unwrapSingleResponse(resp))),
      catchError(err => {
        this.errorService.showErrorMessage('Erro ao atualizar indicação');
        return throwError(() => err);
      })
    );
  }

  delete(id: number, payload?: { userName?: string }): Observable<void> {
    // Some backends accept a body for delete (optional `userName`). Use http.request to allow body.
    return this.http.request<void>('delete', this.financialUrl(`/${id}`), { body: payload }).pipe(
      catchError(err => {
        this.errorService.showErrorMessage('Erro ao remover indicação');
        return throwError(() => err);
      })
    );
  }

  private unwrapSingleResponse(response: any): any {
    if (response == null || typeof response !== 'object') {
      return response;
    }
    const inner = response.data ?? response.Data;
    if (inner != null && typeof inner === 'object' && !Array.isArray(inner)) {
      return inner;
    }
    return response;
  }

  normalizeIndication(item: any): TrafficFineDriverIndication {
    if (!item || typeof item !== 'object') {
      return item;
    }

    const pick = (...keys: string[]): string => {
      for (const key of keys) {
        const value = item[key];
        if (value != null && String(value).trim() !== '') {
          return String(value).trim();
        }
      }
      return '';
    };

    const rawId = item.id ?? item.Id;
    const id =
      rawId != null && rawId !== '' && Number.isFinite(Number(rawId)) && Number(rawId) > 0
        ? Number(rawId)
        : undefined;

    return {
      ...item,
      id,
      driverName: pick('driverName', 'DriverName'),
      documentNumberCPF: pick('documentNumberCPF', 'DocumentNumberCPF'),
      documentNumberRG: pick('documentNumberRG', 'DocumentNumberRG'),
      address: pick('address', 'Address'),
      cep: pick('cep', 'CEP', 'Cep'),
      city: pick('city', 'City'),
      state: pick('state', 'State'),
      userName: pick('userName', 'UserName')
    };
  }

  private mapPagedResponse(response: any): TrafficFineDriverIndicationPagedResult {
    const rawItems = Array.isArray(response?.Items)
      ? response.Items
      : Array.isArray(response?.items)
        ? response.items
        : Array.isArray(response?.data)
          ? response.data
          : Array.isArray(response)
            ? response
            : [];

    const items = rawItems.map((item: any) => this.normalizeIndication(item));
    const page = response?.Page ?? response?.page ?? 1;
    const pageSize = response?.PageSize ?? response?.pageSize ?? items.length;
    const totalItems =
      response?.TotalItems ??
      response?.totalItems ??
      response?.TotalRecords ??
      response?.totalRecords ??
      response?.total ??
      response?.Total ??
      items.length;
    const totalPages = response?.TotalPages ?? response?.totalPages ?? (pageSize > 0 ? Math.ceil(totalItems / pageSize) : 1);

    return {
      items,
      page,
      pageSize,
      totalItems,
      totalPages
    };
  }
}
